import { useContext, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import { isValidEmail, toast } from "../../helper/common";
import ApplicationContext from "../../context/application/ApplicationContext";
import AuthContext from "../../context/auth/AuthContext";
import SkorProtectSelector from "../SkorProtectSelector/SkorProtectSelector";
import { Avatar, Badge, Button, Icon, Modal, TextField } from "../design";
import "./NewApplicationModal.css";

const NewApplicationModal = ({ isOpen, onClose, onSuccess }) => {
  const { _t } = useTranslator();
  const { createApplication, myApplications } = useContext(ApplicationContext);
  const { user } = useContext(AuthContext);

  const currentLang = localStorage.getItem("i18nextLng").toLowerCase() || "en";

  const defaultTitle =
    currentLang === "es"
      ? `Aplicación de ${user?.fname}`
      : `${user?.fname}'s Application`;

  const [appTitle, setAppTitle] = useState(defaultTitle);
  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [participants, setParticipants] = useState([]);
  const [emailError, setEmailError] = useState("");
  const [creating, setCreating] = useState(false);
  const [skorProtect, setSkorProtect] = useState(true);

  const handleAddEmail = () => {
    const trimmedName = nameInput.trim();
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (!trimmedName) {
      setEmailError(_t("nameRequired", "Full name is required"));
      return;
    }
    if (!trimmedEmail) return;
    if (!isValidEmail(trimmedEmail)) {
      setEmailError(_t("invalidEmail"));
      return;
    }
    if (trimmedEmail === user?.email?.toLowerCase()) {
      setEmailError(_t("cannotAddYourself"));
      return;
    }
    if (participants.some((p) => p.email === trimmedEmail)) {
      setEmailError(_t("emailAlreadyAdded"));
      return;
    }
    if (participants.length >= 4) {
      setEmailError(
        _t("maxParticipantsReached", "Maximum of 4 participants allowed")
      );
      return;
    }
    setParticipants((prev) => [
      ...prev,
      { name: trimmedName, email: trimmedEmail },
    ]);
    setNameInput("");
    setEmailInput("");
    setEmailError("");
  };

  const handleRemoveEmail = (email) => {
    setParticipants((prev) => prev.filter((p) => p.email !== email));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleCreate = async () => {
    if (participants.length === 0) {
      toast.error(_t("addAtLeastOneEmail"));
      return;
    }

    const newEmails = participants.map((p) => p.email.toLowerCase()).sort();
    const currentUserEmail = user?.email?.toLowerCase();
    const hasDuplicate = (myApplications ?? []).some((app) => {
      const existing = (app.participants ?? [])
        .map((p) => p.email?.toLowerCase())
        .filter((e) => e && e !== currentUserEmail)
        .sort();
      return (
        existing.length === newEmails.length &&
        existing.every((e, i) => e === newEmails[i])
      );
    });
    if (hasDuplicate) {
      toast.error(_t("alreadyExistingApplication"));
      return;
    }
    try {
      setCreating(true);
      await createApplication({
        participants,
        title: appTitle.trim() || defaultTitle,
        skor_protect: skorProtect,
      });
      onSuccess();
      handleClose();
      toast.success(_t("applicationCreatedSuccess"));
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? _t("failedToCreateApplication")
      );
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setAppTitle(defaultTitle);
    setNameInput("");
    setEmailInput("");
    setParticipants([]);
    setEmailError("");
    setSkorProtect(true);
    onClose();
  };

  const handleTitleBlur = () => {
    if (!appTitle.trim()) setAppTitle(defaultTitle);
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={_t("newApplication")}
      closeLabel={_t("close")}
      align="start"
      actionsLayout="end"
      actions={
        <>
          <Button variant="secondary" size="lg" onClick={handleClose}>
            {_t("cancel")}
          </Button>
          <Button size="lg" loading={creating} onClick={handleCreate}>
            {_t("createApplication")}
          </Button>
        </>
      }
    >
      <div className="new-application-form">
        <TextField
          id="new-application-title"
          label={_t("applicationName", "Application name")}
          value={appTitle}
          onChange={(e) => setAppTitle(e.target.value)}
          onBlur={handleTitleBlur}
        />

        <div className="new-application-invite">
          <div>
            <h3 className="new-application-invite__title">
              {_t("inviteFlatmatesByEmail")}
            </h3>
            <p className="new-application-invite__text">
              {_t(
                "typeEmailAndPressEnter",
                "The invited flatmates will receive a request to confirm they accept being part of this application."
              )}
            </p>
          </div>

          <div className="new-application-invite__row">
            <TextField
              id="new-application-name"
              placeholder={_t("fullName", "Full Name")}
              value={nameInput}
              error={emailError ? true : undefined}
              onChange={(e) => {
                setNameInput(e.target.value);
                setEmailError("");
              }}
              onKeyDown={handleKeyDown}
            />
            <TextField
              id="new-application-email"
              type="email"
              placeholder="name@email.com"
              value={emailInput}
              error={emailError || undefined}
              onChange={(e) => {
                setEmailInput(e.target.value.toLowerCase());
                setEmailError("");
              }}
              onKeyDown={handleKeyDown}
            />
            <Button
              variant="secondary"
              size="lg"
              className="new-application-invite__add"
              onClick={handleAddEmail}
              disabled={!emailInput.trim() || !nameInput.trim()}
            >
              {_t("add")}
            </Button>
          </div>
        </div>

        {participants.length > 0 && (
          <div className="new-application-people">
            {participants.map(({ name, email }) => (
              <div key={email} className="new-application-person">
                <Avatar name={name} size="md" />
                <div className="new-application-person__info">
                  <span className="new-application-person__name">{name}</span>
                  <span className="new-application-person__email">{email}</span>
                </div>
                <Badge tone="neutral">
                  {_t("invitationWillBeSent", "Invitation will be sent")}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label={_t("remove")}
                  iconStart={<Icon name="close" />}
                  onClick={() => handleRemoveEmail(email)}
                />
              </div>
            ))}
          </div>
        )}

        <SkorProtectSelector value={skorProtect} onChange={setSkorProtect} />
      </div>
    </Modal>
  );
};

export default NewApplicationModal;
